import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import Navbar from '../components/Navbar';
import { flag } from '../utils/countryFlags';
import { normalize } from '../utils/textUtils';
import GlobalNavigation from '../components/GlobalNavigation';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import {
  Plane, Package, TrendingUp, DollarSign, Star, Filter, Clock, CheckCircle, MapPin,
  Wallet, History, Eye, XCircle, Mail, ExternalLink, Shield, Zap, Target, BarChart3,
  ArrowRight, Lightbulb, Award, ChevronDown, ChevronUp, Percent, AlertTriangle,
  TrendingDown, Rocket, Edit, Power
} from 'lucide-react';
import axios from 'axios';
import SupportContact from '../components/SupportContact';

// ─── CONSTANTS ───────────────────────────────────────────────────────
const statusLabels = {
  pending_admin_validation: 'En attente de validation',
  pending_payment: 'En attente de paiement',
  paid: 'Paiement confirmé',
  awaiting_pickup_seller: 'En attente récupération',
  received_admin_departure: 'Reçu Admin - Départ',
  in_transit: 'En transit',
  received_admin_destination: 'Reçu Admin - Destination',
  delivered: 'Livré',
  completed: 'Complété',
  cancelled: 'Annulé',
  refunded: 'Remboursé',
  disputed: 'En litige'
};

const listingStatusLabels = {
  pending_verification: 'En approbation', active: 'Active', reserved: 'Réservée',
  closed: 'Fermée', expired: 'Expirée', canceled: 'Annulée', cancelled: 'Annulée', rejected: 'Rejetée'
};

const listingStatusColors = {
  pending_verification: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
  active: 'bg-green-500/20 text-green-500 border-green-500/30',
  reserved: 'bg-amber-500/20 text-amber-500 border-amber-500/30',
  closed: 'bg-gray-500/20 text-gray-500 border-gray-500/30',
  expired: 'bg-gray-500/20 text-gray-500 border-gray-500/30',
  canceled: 'bg-red-500/20 text-red-500 border-red-500/30',
  cancelled: 'bg-red-500/20 text-red-500 border-red-500/30',
  rejected: 'bg-red-500/20 text-red-500 border-red-500/30'
};

const TYPE_LABELS_SHORT = { kilos: 'kg', sacs: 'sac', enveloppes: 'env.', telephones: 'tél.', ordinateurs: 'ordi.', sac_23kg: 'sac 23kg' };
function formatOrderBaggage(order) {
  if (order.selected_items && order.selected_items.length > 0) {
    return order.selected_items.map(it => it.quantity + ' ' + (TYPE_LABELS_SHORT[it.type] || it.type)).join(', ');
  }
  if (order.baggage_type) return order.quantity + ' ' + (TYPE_LABELS_SHORT[order.baggage_type] || order.baggage_type);
  return order.quantity + ' unité(s)';
}

const statusColors = {
  pending_admin_validation: 'bg-amber-500/20 text-amber-600',
  pending_payment: 'bg-gray-500/20 text-gray-500',
  paid: 'bg-amber-500/20 text-amber-500',
  awaiting_pickup_seller: 'bg-orange-500/20 text-orange-500',
  received_admin_departure: 'bg-teal-500/20 text-teal-500',
  in_transit: 'bg-violet-500/20 text-violet-500',
  received_admin_destination: 'bg-sky-500/20 text-sky-500',
  delivered: 'bg-green-500/20 text-green-500',
  completed: 'bg-emerald-500/20 text-emerald-500',
  cancelled: 'bg-red-500/20 text-red-500',
  refunded: 'bg-amber-500/20 text-amber-500',
  disputed: 'bg-red-500/20 text-red-500'
};

const countries = ['France', 'Sénégal', 'Côte d\'Ivoire', 'Cameroun', 'Maroc', 'Canada', 'États-Unis', 'Belgique', 'Suisse', 'Allemagne'];

// ─── HELPERS ─────────────────────────────────────────────────────────
const getDemandLevel = (flight) => {
  const ordersCount = flight.orders_active || 0;
  const totalQty = flight.quantity || 1;
  const availableQty = flight.available_quantity ?? totalQty;
  const fillRate = ((totalQty - availableQty) / totalQty) * 100;

  if (ordersCount >= 3 || fillRate >= 70) return { level: 'high', label: 'Élevée', color: 'bg-green-500/15 text-green-400 border-green-500/30', icon: TrendingUp };
  if (ordersCount >= 1 || fillRate >= 30) return { level: 'medium', label: 'Moyenne', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30', icon: Target };
  return { level: 'low', label: 'Faible', color: 'bg-red-500/15 text-red-400 border-red-500/30', icon: TrendingDown };
};

function getMinPriceSeller(listing) {
  if (listing?.baggage_items?.length) {
    const prices = listing.baggage_items
      .filter(i => i.price_per_unit > 0)
      .map(i => i.price_per_unit);
    if (prices.length) return Math.min(...prices);
  }
  return listing?.price_per_unit ?? 0;
}

const getSuggestions = (flight, avgPrices) => {
  const suggestions = [];
  const totalQty = flight.quantity || 1;
  const availableQty = flight.available_quantity ?? totalQty;
  const fillRate = ((totalQty - availableQty) / totalQty) * 100;
  const routeKey = `${flight.departure_country} → ${flight.destination_country}`;
  const routeData = avgPrices?.[routeKey];

  if (routeData && flight.price_per_unit > routeData.avg_price * 1.15) {
    suggestions.push({ type: 'price_high', text: `Réduisez votre prix pour vendre plus vite (moyenne : ${routeData.avg_price.toFixed(0)} ${flight.currency || 'CAD'})`, icon: TrendingDown, color: 'text-amber-400' });
  } else if (routeData && flight.price_per_unit < routeData.avg_price * 0.9 && fillRate < 50) {
    suggestions.push({ type: 'price_low', text: `Augmentez votre prix de +${Math.round(routeData.avg_price * 0.1)} ${flight.currency || 'CAD'} (marge possible)`, icon: TrendingUp, color: 'text-green-400' });
  }

  if (fillRate === 0 && (flight.orders_active || 0) === 0) {
    suggestions.push({ type: 'no_orders', text: 'Aucune commande encore. Partagez votre annonce !', icon: Rocket, color: 'text-primary' });
  }

  if (fillRate >= 80) {
    suggestions.push({ type: 'almost_full', text: 'Presque complet ! Créez une nouvelle annonce pour le même vol.', icon: Zap, color: 'text-emerald-400' });
  }

  return suggestions;
};

// ─── SCORE BADGE ─────────────────────────────────────────────────────
const ScoreRing = ({ score }) => {
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#22c55e' : score >= 50 ? '#D4AF37' : '#ef4444';

  return (
    <svg width="100" height="100" viewBox="0 0 100 100" className="flex-shrink-0">
      <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="6" className="text-foreground/[0.06]" />
      <circle cx="50" cy="50" r="40" fill="none" stroke={color} strokeWidth="6" strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round" transform="rotate(-90 50 50)" className="transition-all duration-1000" />
      <text x="50" y="46" textAnchor="middle" fill={color} fontSize="22" fontWeight="bold">{score}</text>
      <text x="50" y="62" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="9">/100</text>
    </svg>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────
const SellerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, API } = useAuth();
  const { formatPrice } = useCurrency();

  const urlTab = searchParams.get('tab') || 'flights';
  const highlightOrderId = searchParams.get('order');

  const [stats, setStats] = useState(null);
  const [flights, setFlights] = useState([]);
  const [orders, setOrders] = useState([]);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const [opportunities, setOpportunities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(urlTab);
  const [filters, setFilters] = useState({ status: '', departureCountry: '', destinationCountry: '', departure: '', destination: '', travelDate: '' });
  const [flightFilter, setFlightFilter] = useState('active');

  useEffect(() => {
    if (highlightOrderId && orders.length > 0) {
      const element = document.getElementById(`order-${highlightOrderId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
        setTimeout(() => element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2'), 3000);
      }
    }
  }, [highlightOrderId, orders]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightFilter, activeTab]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, flightsRes, ordersRes, oppsRes] = await Promise.all([
        axios.get(`${API}/seller/stats`, { headers }),
        axios.get(`${API}/seller/flights?status=${flightFilter}`, { headers }),
        axios.get(`${API}/orders/seller`, { headers }),
        axios.get(`${API}/seller/opportunities`, { headers }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setFlights(flightsRes.data);
      setOpportunities(oppsRes.data);

      const activeStatuses = ['pending_admin_validation', 'paid', 'awaiting_pickup_seller', 'received_admin_departure', 'in_transit', 'received_admin_destination', 'delivered'];
      const allOrders = ordersRes.data;
      setOrders(allOrders.filter(o => activeStatuses.includes(o.current_status) && !(o.current_status === 'delivered' && o.buyer_confirmed_delivered)));
      setHistoryOrders(allOrders.filter(o => o.current_status === 'completed' || (o.current_status === 'delivered' && o.buyer_confirmed_delivered)));
      setCancelledOrders(allOrders.filter(o => ['cancelled', 'refunded', 'rejected'].includes(o.current_status)));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = (orderList) => {
    return orderList.filter(o => {
      if (filters.status && filters.status !== 'all' && o.current_status !== filters.status) return false;
      if (filters.departureCountry && filters.departureCountry !== 'all' && o.departure_country !== filters.departureCountry) return false;
      if (filters.destinationCountry && filters.destinationCountry !== 'all' && o.destination_country !== filters.destinationCountry) return false;
      if (filters.departure && !normalize(o.departure_city).includes(normalize(filters.departure))) return false;
      if (filters.destination && !normalize(o.destination_city).includes(normalize(filters.destination))) return false;
      if (filters.travelDate && o.travel_date !== filters.travelDate) return false;
      return true;
    });
  };

  const filteredOrders = filterOrders(orders);
  const filteredHistory = filterOrders(historyOrders);
  const filteredCancelled = filterOrders(cancelledOrders);

  const XAF_RATE = 415;
  const toCAD = (order) => { const a = order.seller_amount || 0; return (order.currency === 'XAF' || order.currency === 'XOF') ? a / XAF_RATE : a; };
  const pendingPayments = orders.filter(o => o.escrow_status === 'held');
  const pendingPaymentTotal = pendingPayments.reduce((sum, o) => sum + toCAD(o), 0);

  // Fill rate across all active flights
  const activeFlight = flights.filter(f => f.status === 'active');
  const totalCap = activeFlight.reduce((s, f) => s + (f.quantity || 0), 0);
  const totalUsed = activeFlight.reduce((s, f) => s + ((f.quantity || 0) - (f.available_quantity ?? f.quantity)), 0);
  const fillRate = totalCap > 0 ? Math.round((totalUsed / totalCap) * 100) : 0;

  // Revenue potential
  const revenuePotential = activeFlight.reduce((s, f) => s + ((f.available_quantity ?? 0) * (f.price_per_unit || 0)), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="seller-dashboard">
      <Navbar />
      <GlobalNavigation />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-[-80px] right-[-40px] w-[400px] h-[400px] rounded-full bg-[#D4AF37]/[0.04] blur-[100px]" />
        <div className="absolute bottom-[-60px] left-[20%] w-[300px] h-[300px] rounded-full bg-emerald-500/[0.03] blur-[80px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <p className="text-[#D4AF37]/70 text-xs tracking-[0.2em] uppercase mb-2">Espace Vendeur</p>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2" data-testid="seller-hero-title">
                Boostez vos ventes aujourd'hui
              </h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Target className="w-4 h-4 text-emerald-400" />
                  <strong className="text-foreground">{opportunities?.total_active_demand || 0}</strong> demandes actives
                </span>
                <span className="flex items-center gap-1.5">
                  <Zap className="w-4 h-4 text-[#D4AF37]" />
                  <strong className="text-foreground">{activeFlight.length}</strong> annonces en ligne
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setActiveTab('orders')}
                className="bg-muted/40 border-border text-muted-foreground hover:text-foreground rounded-xl"
                data-testid="view-orders-btn"
              >
                <Package className="w-4 h-4 mr-2" /> Mes commandes
              </Button>
              <Button
                onClick={() => navigate('/create-listing')}
                className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl"
                data-testid="new-listing-btn"
              >
                <Plane className="w-4 h-4 mr-2" /> Nouvelle annonce
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">

        {/* ── STATS GRID ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="seller-stats-grid">
          {[
            { icon: Plane, label: 'Vols actifs', value: stats?.active_flights || 0, accent: 'text-[#D4AF37]', onClick: () => { setActiveTab('flights'); setFlightFilter('active'); } },
            { icon: Package, label: 'En cours', value: stats?.pending_orders || 0, accent: 'text-orange-400', onClick: () => setActiveTab('orders') },
            { icon: CheckCircle, label: 'Complétées', value: stats?.completed_orders || 0, accent: 'text-green-400', onClick: () => setActiveTab('history') },
            { icon: Percent, label: 'Taux remplissage', value: `${fillRate}%`, accent: fillRate >= 60 ? 'text-green-400' : fillRate >= 30 ? 'text-amber-400' : 'text-red-400', bar: fillRate },
            { icon: Wallet, label: 'En séquestre', value: formatPrice(stats?.pending_earnings || 0), accent: 'text-amber-400', onClick: () => setActiveTab('orders') },
            { icon: DollarSign, label: 'Gains totaux', value: formatPrice(stats?.total_earnings || 0), accent: 'text-green-400', onClick: () => setActiveTab('history') },
          ].map(s => (
            <button
              key={s.label}
              className={`bg-muted/30 border border-border rounded-xl p-4 text-left transition-all hover:border-white/[0.12] ${s.onClick ? 'cursor-pointer' : ''}`}
              onClick={s.onClick}
              data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <s.icon className={`w-6 h-6 ${s.accent} mb-2`} />
              <p className="text-xl font-bold text-foreground">{s.value}</p>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{s.label}</p>
              {s.bar !== undefined && (
                <div className="mt-2 h-1.5 bg-muted/50 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${fillRate >= 60 ? 'bg-green-500' : fillRate >= 30 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${s.bar}%` }} />
                </div>
              )}
              {s.onClick && <p className="text-[10px] text-[#D4AF37]/60 mt-1">Cliquer pour voir</p>}
            </button>
          ))}
        </div>

        {/* ── SECOND ROW: Score + Revenue Potential + Demand ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          {/* Seller Score */}
          <div className="bg-muted/30 border border-border rounded-xl p-5 flex items-center gap-5" data-testid="seller-score-card">
            <ScoreRing score={opportunities?.seller_score || 0} />
            <div>
              <p className="text-sm font-semibold text-foreground mb-0.5">Score vendeur</p>
              <Badge className="text-[10px] bg-muted/50 text-muted-foreground border-border mb-2">{opportunities?.grade || stats?.grade || 'Nouveau'}</Badge>
              <div className="space-y-1">
                {(opportunities?.seller_score || 0) < 40 && <p className="text-[11px] text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Complétez plus de commandes</p>}
                {(opportunities?.seller_rating || 0) < 4 && (opportunities?.total_reviews || 0) > 0 && <p className="text-[11px] text-amber-400 flex items-center gap-1"><Star className="w-3 h-3" /> Améliorez vos évaluations</p>}
                {(opportunities?.seller_score || 0) >= 80 && <p className="text-[11px] text-green-400 flex items-center gap-1"><Award className="w-3 h-3" /> Excellent score !</p>}
                {(opportunities?.total_cancelled || 0) > 2 && <p className="text-[11px] text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> Réduisez les annulations</p>}
              </div>
            </div>
          </div>

          {/* Revenue Potential */}
          <div className="bg-muted/30 border border-border rounded-xl p-5" data-testid="revenue-potential-card">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <p className="text-sm font-semibold text-foreground">Revenus potentiels</p>
            </div>
            <p className="text-2xl font-bold text-emerald-400 mb-1">{formatPrice(revenuePotential)}</p>
            <p className="text-[11px] text-muted-foreground">Basé sur vos places disponibles au prix actuel</p>
            {revenuePotential === 0 && (
              <Button size="sm" className="mt-3 bg-[#D4AF37] hover:bg-[#C4A030] text-black text-xs rounded-lg" onClick={() => navigate('/create-listing')}>
                <Plane className="w-3 h-3 mr-1" /> Créer une annonce
              </Button>
            )}
          </div>

          {/* Demand Level */}
          <div className="bg-muted/30 border border-border rounded-xl p-5" data-testid="demand-level-card">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-5 h-5 text-[#D4AF37]" />
              <p className="text-sm font-semibold text-foreground">Niveau de demande</p>
            </div>
            <p className="text-2xl font-bold text-[#D4AF37] mb-1">{opportunities?.total_active_demand || 0} demandes</p>
            <p className="text-[11px] text-muted-foreground mb-2">Commandes en attente sur la plateforme</p>
            {(opportunities?.top_demand_routes || []).slice(0, 2).map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs text-muted-foreground py-1 border-t border-white/[0.04]">
                <span>{r.route}</span>
                <Badge className="text-[9px] bg-green-500/10 text-green-400 border-green-500/20">{r.orders} demande{r.orders > 1 ? 's' : ''}</Badge>
              </div>
            ))}
          </div>
        </div>

        {/* ── MAIN TABS ── */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-muted/40 border border-border">
            <TabsTrigger value="flights"><Plane className="w-4 h-4 mr-2" />Mes Vols ({flights.length})</TabsTrigger>
            <TabsTrigger value="orders"><Package className="w-4 h-4 mr-2" />Ventes & Commandes ({orders.length})</TabsTrigger>
            <TabsTrigger value="history"><History className="w-4 h-4 mr-2" />Historique</TabsTrigger>
            <TabsTrigger value="cancelled" data-testid="seller-tab-cancelled"><XCircle className="w-4 h-4 mr-2" />Annulées ({cancelledOrders.length})</TabsTrigger>
          </TabsList>

          {/* ═══ FLIGHTS TAB ═══ */}
          <TabsContent value="flights">
            <div className="flex items-center gap-4 mb-6">
              <Select value={flightFilter} onValueChange={setFlightFilter}>
                <SelectTrigger className="w-48 bg-muted/40 border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes mes annonces</SelectItem>
                  <SelectItem value="active">Actives uniquement</SelectItem>
                  <SelectItem value="past">Passées/Fermées</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              {flights.length === 0 ? (
                <div className="bg-muted/30 border border-border rounded-xl py-16 text-center">
                  <Plane className="w-12 h-12 mx-auto mb-4 text-foreground/20" />
                  <p className="text-muted-foreground mb-4">
                    {flightFilter === 'all' ? 'Aucune annonce' : flightFilter === 'active' ? 'Aucune annonce active' : 'Aucune annonce passée'}
                  </p>
                  <Button onClick={() => navigate('/create-listing')} className="bg-[#D4AF37] hover:bg-[#C4A030] text-black rounded-xl">Créer une annonce</Button>
                </div>
              ) : (
                flights.map(flight => {
                  const demand = getDemandLevel(flight);
                  const DemandIcon = demand.icon;
                  const suggestions = getSuggestions(flight, opportunities?.route_avg_prices);
                  const totalQty = flight.quantity || 1;
                  const availableQty = flight.available_quantity ?? totalQty;
                  const flightFillRate = Math.round(((totalQty - availableQty) / totalQty) * 100);

                  return (
                    <div key={flight.id} className="bg-muted/30 border border-border rounded-xl overflow-hidden hover:border-white/[0.12] transition-all" data-testid={`flight-card-${flight.id}`}>
                      <div className="p-4 sm:p-5">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge className={listingStatusColors[flight.status] || 'bg-gray-500/20 text-gray-500'}>
                                {listingStatusLabels[flight.status] || flight.status}
                              </Badge>
                              <Badge className={`text-[10px] border ${demand.color}`} data-testid={`demand-${flight.id}`}>
                                <DemandIcon className="w-3 h-3 mr-1" /> Demande {demand.label}
                              </Badge>
                              <span className="text-xs text-foreground/30">{flight.airline}</span>
                            </div>
                            <h3 className="font-medium text-lg text-foreground">
                              {flight.departure_city} ({flag(flight.departure_country)} {flight.departure_country}) → {flight.destination_city} ({flag(flight.destination_country)} {flight.destination_country})
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {new Date(flight.travel_date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                            </p>

                            {/* Fill rate bar */}
                            <div className="mt-3 max-w-xs">
                              <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                                <span>Remplissage</span>
                                <span className={flightFillRate >= 70 ? 'text-green-400' : flightFillRate >= 30 ? 'text-amber-400' : 'text-muted-foreground'}>{flightFillRate}%</span>
                              </div>
                              <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${flightFillRate >= 70 ? 'bg-green-500' : flightFillRate >= 30 ? 'bg-amber-500' : 'bg-white/20'}`} style={{ width: `${flightFillRate}%` }} />
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 sm:gap-6">
                            <div className="text-center">
                              {flight.baggage_items && flight.baggage_items.length > 0 ? (
                                <div className="flex flex-col gap-0.5">
                                  {flight.baggage_items.map((item, idx) => (
                                    <p key={idx} className="text-sm font-mono text-foreground">
                                      <span className="font-bold">{item.available_quantity ?? item.quantity}/{item.quantity}</span>
                                      <span className="text-xs text-muted-foreground ml-1">
                                        {item.type === 'kilos' ? 'kg' : item.type === 'enveloppes' ? 'env.' : item.type === 'telephones' ? 'tél.' : item.type === 'ordinateurs' ? 'ordi.' : item.type === 'sac_23kg' ? 'sac 23kg' : item.type}
                                      </span>
                                    </p>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xl font-bold text-foreground">{flight.available_quantity}/{flight.quantity}</p>
                              )}
                              <p className="text-[10px] text-muted-foreground">Disponible</p>
                            </div>
                            <div className="text-center">
                              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">À partir de</p>
                              <p className="text-xl font-bold text-[#D4AF37]">{formatPrice(getMinPriceSeller(flight))}</p>
                              <p className="text-[10px] text-muted-foreground">par {flight.baggage_type === 'kilos' ? 'kg' : flight.baggage_type === 'sacs' ? 'sac' : flight.baggage_type === 'enveloppes' ? 'enveloppe' : flight.baggage_type === 'telephones' ? 'tél.' : flight.baggage_type === 'ordinateurs' ? 'ordi.' : flight.baggage_type === 'sac_23kg' ? 'sac 23kg' : flight.baggage_type}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-bold text-green-400">{flight.orders_active || 0}</p>
                              <p className="text-[10px] text-muted-foreground">Commandes</p>
                            </div>
                          </div>
                        </div>

                        {/* Suggestions */}
                        {suggestions.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                            {suggestions.map((s, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                <Lightbulb className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                                <span className={s.color}>{s.text}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Quick Actions */}
                      <div className="flex border-t border-border">
                        <button
                          className="flex-1 py-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all border-r border-border"
                          onClick={() => navigate(`/listings/${flight.id}`)}
                          data-testid={`view-listing-${flight.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" /> Voir
                        </button>
                        <button
                          className="flex-1 py-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-[#D4AF37] hover:bg-[#D4AF37]/[0.04] transition-all border-r border-border"
                          onClick={() => navigate(`/listings/${flight.id}?action=edit`)}
                          data-testid={`edit-listing-${flight.id}`}
                        >
                          <Edit className="w-3.5 h-3.5" /> Modifier
                        </button>
                        <button
                          className="flex-1 py-3 flex items-center justify-center gap-1.5 text-xs text-red-400/60 hover:text-red-400 hover:bg-red-500/[0.04] transition-all"
                          onClick={async () => {
                            if (!window.confirm('Voulez-vous vraiment désactiver cette annonce ?')) return;
                            try {
                              await axios.put(`${API}/listings/${flight.id}`, { status: 'cancelled' }, { headers: { Authorization: `Bearer ${token}` } });
                              fetchData();
                            } catch (e) { console.error(e); }
                          }}
                          data-testid={`disable-listing-${flight.id}`}
                        >
                          <Power className="w-3.5 h-3.5" /> Désactiver
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

          </TabsContent>

          {/* ═══ ORDERS TAB ═══ */}
          <TabsContent value="orders">
            <Card className="mb-6 bg-muted/30 border-border">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-3">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium text-foreground/70">Filtres</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <Select value={filters.status} onValueChange={v => setFilters({...filters, status: v})}>
                    <SelectTrigger><SelectValue placeholder="Statut" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      {Object.entries(statusLabels).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filters.departureCountry} onValueChange={v => setFilters({...filters, departureCountry: v})}>
                    <SelectTrigger><SelectValue placeholder="Pays départ" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous pays</SelectItem>
                      {countries.map(c => <SelectItem key={c} value={c} textValue={c}>{flag(c)} {c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filters.destinationCountry} onValueChange={v => setFilters({...filters, destinationCountry: v})}>
                    <SelectTrigger><SelectValue placeholder="Pays arrivée" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous pays</SelectItem>
                      {countries.map(c => <SelectItem key={c} value={c} textValue={c}>{flag(c)} {c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Ville départ" value={filters.departure} onChange={e => setFilters({...filters, departure: e.target.value})} />
                  <Input placeholder="Ville arrivée" value={filters.destination} onChange={e => setFilters({...filters, destination: e.target.value})} />
                  <Input type="date" value={filters.travelDate} onChange={e => setFilters({...filters, travelDate: e.target.value})} />
                </div>
                {(filters.status || filters.departureCountry || filters.destinationCountry || filters.departure || filters.destination || filters.travelDate) && (
                  <Button variant="ghost" size="sm" className="mt-2 text-muted-foreground" onClick={() => setFilters({ status: '', departureCountry: '', destinationCountry: '', departure: '', destination: '', travelDate: '' })}>
                    Réinitialiser les filtres
                  </Button>
                )}
              </CardContent>
            </Card>

            {pendingPayments.length > 0 && (
              <div className="mb-6 bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Wallet className="w-6 h-6 text-amber-400" />
                  <div>
                    <p className="font-medium text-foreground">Paiements en séquestre</p>
                    <p className="text-sm text-muted-foreground">{pendingPayments.length} commande(s) en attente de confirmation</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-amber-400">{formatPrice(pendingPaymentTotal)}</p>
              </div>
            )}

            <div className="space-y-4">
              {filteredOrders.length === 0 ? (
                <div className="bg-muted/30 border border-border rounded-xl py-12 text-center">
                  <Package className="w-12 h-12 mx-auto mb-4 text-foreground/20" />
                  <p className="text-muted-foreground">Aucune commande active</p>
                </div>
              ) : (
                filteredOrders.map(order => (
                  <Card key={order.id} id={`order-${order.id}`} className="bg-muted/30 border-border hover:border-white/[0.12] transition-colors">
                    <CardContent className="py-4">
                      <div className="flex flex-col lg:flex-row gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={statusColors[order.current_status]}>{statusLabels[order.current_status] || order.current_status}</Badge>
                            <span className="text-foreground/30">|</span>
                            <span className="text-sm text-muted-foreground">{formatOrderBaggage(order)}</span>
                          </div>
                          <p className="text-lg font-medium text-foreground">{flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}</p>
                          <p className="text-sm text-muted-foreground">Vol du {new Date(order.travel_date + 'T12:00:00').toLocaleDateString('fr-FR')}</p>
                          {order.listing_id && (
                            <Link to={`/listings/${order.listing_id}`} className="text-[#D4AF37] hover:underline text-xs inline-flex items-center gap-1 mt-1">
                              <ExternalLink className="w-3 h-3" />Voir l'annonce
                            </Link>
                          )}
                          <div className="mt-2">
                            <p className="text-xl font-bold text-green-400">+{formatPrice(order.seller_amount)}</p>
                            {order.seller_commission > 0 && (
                              <p className="text-xs text-muted-foreground">Après commission Kacos ({order.seller_commission_rate || 10}%): -{formatPrice(order.seller_commission)}</p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              {order.escrow_status === 'held' ? 'En séquestre - libéré après confirmation acheteur' : order.escrow_status === 'released' ? 'Paiement libéré' : 'En attente'}
                            </p>
                          </div>
                          <div className="mt-2 flex items-center gap-1.5 text-xs">
                            <Shield className={`w-3 h-3 ${order.insurance_opt_in ? 'text-primary' : 'text-foreground/30'}`} />
                            {order.insurance_opt_in
                              ? <span className="text-primary">Assuré — Valeur déclarée : {formatPrice(order.declared_value || 0)}</span>
                              : <span className="text-foreground/30">Sans assurance{order.declared_value > 0 ? ` — Valeur déclarée : ${formatPrice(order.declared_value)}` : ''}</span>
                            }
                          </div>
                        </div>
                        <div className="lg:w-64 border-t lg:border-t-0 lg:border-l border-border pt-4 lg:pt-0 lg:pl-4">
                          <p className="text-xs text-foreground/30 mb-2">Suivi logistique (géré par admin)</p>
                          <div className="space-y-2">
                            {['paid', 'awaiting_pickup_seller', 'received_admin_departure', 'in_transit', 'received_admin_destination', 'delivered'].map(s => {
                              const statusOrder = { paid: 1, awaiting_pickup_seller: 2, received_admin_departure: 3, in_transit: 4, received_admin_destination: 5, delivered: 6 };
                              const currentOrder = statusOrder[order.current_status] ?? 0;
                              const thisOrder = statusOrder[s];
                              const isActive = thisOrder <= currentOrder;
                              const isCurrent = s === order.current_status;
                              return (
                                <div key={s} className={`flex items-center gap-2 text-xs ${isActive ? 'text-foreground/70' : 'text-foreground/20'}`}>
                                  <div className={`w-2.5 h-2.5 rounded-full ${isCurrent ? 'bg-[#D4AF37] animate-pulse' : isActive ? 'bg-green-500' : 'bg-muted'}`} />
                                  <span className={isCurrent ? 'font-medium' : ''}>{statusLabels[s]}</span>
                                </div>
                              );
                            })}
                          </div>
                          {order.current_status === 'delivered' && (
                            <div className="mt-3 pt-3 border-t border-border">
                              {order.buyer_confirmed_delivered
                                ? <Badge className="bg-green-500/20 text-green-400 text-xs">Confirmé par acheteur</Badge>
                                : <Badge className="bg-amber-500/20 text-amber-400 text-xs">Confirmation acheteur (48h)</Badge>
                              }
                            </div>
                          )}
                          <div className="mt-3 pt-3 border-t border-border">
                            <SupportContact orderId={order.id} triggerButton={
                              <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground hover:text-foreground" data-testid={`seller-contact-support-${order.id}`}>
                                <Mail className="w-3.5 h-3.5 mr-1" />Contacter le support
                              </Button>
                            } />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* ═══ HISTORY TAB ═══ */}
          <TabsContent value="history">
            <Card className="mb-6 bg-muted/30 border-border">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-3"><Filter className="w-4 h-4 text-muted-foreground" /><span className="font-medium text-foreground/70">Filtres Historique</span></div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Select value={filters.departureCountry} onValueChange={v => setFilters({...filters, departureCountry: v})}>
                    <SelectTrigger><SelectValue placeholder="Pays départ" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tous pays</SelectItem>{countries.map(c => <SelectItem key={c} value={c} textValue={c}>{flag(c)} {c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={filters.destinationCountry} onValueChange={v => setFilters({...filters, destinationCountry: v})}>
                    <SelectTrigger><SelectValue placeholder="Pays arrivée" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tous pays</SelectItem>{countries.map(c => <SelectItem key={c} value={c} textValue={c}>{flag(c)} {c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="date" value={filters.travelDate} onChange={e => setFilters({...filters, travelDate: e.target.value})} placeholder="Date" />
                  <Button variant="outline" className="bg-muted/40 border-border text-muted-foreground" onClick={() => setFilters({ status: '', departureCountry: '', destinationCountry: '', departure: '', destination: '', travelDate: '' })}>Réinitialiser</Button>
                </div>
              </CardContent>
            </Card>
            <div className="space-y-4">
              {filteredHistory.length === 0 ? (
                <div className="bg-muted/30 border border-border rounded-xl py-12 text-center">
                  <History className="w-12 h-12 mx-auto mb-4 text-foreground/20" /><p className="text-muted-foreground">Aucun historique</p>
                </div>
              ) : (
                filteredHistory.map(order => (
                  <Card key={order.id} className="bg-muted/30 border-border opacity-80 hover:opacity-100 transition-opacity">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium flex items-center gap-1 text-foreground/70"><Star className="w-3 h-3 text-yellow-500" />Note: {order.buyer_rating?.toFixed(1) || 'N/A'}</span>
                            <Badge className={statusColors[order.current_status]}>{statusLabels[order.current_status]}</Badge>
                          </div>
                          <p className="text-foreground">{flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}</p>
                          <p className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString('fr-FR')}</p>
                          {order.listing_id && <Link to={`/listings/${order.listing_id}`} className="text-[#D4AF37] hover:underline text-xs inline-flex items-center gap-1 mt-0.5"><ExternalLink className="w-3 h-3" />Voir l'annonce</Link>}
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-400">+{formatPrice(order.seller_amount)}</p>
                          <p className="text-xs text-muted-foreground">{order.escrow_status === 'released' ? 'Paiement reçu' : order.escrow_status}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* ═══ CANCELLED TAB ═══ */}
          <TabsContent value="cancelled" data-testid="seller-cancelled-orders-tab">
            <div className="mb-6 bg-red-500/5 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
              <XCircle className="w-6 h-6 text-red-400" />
              <div>
                <p className="font-medium text-foreground">Commandes annulées ou remboursées</p>
                <p className="text-sm text-muted-foreground">{cancelledOrders.length} commande(s)</p>
              </div>
            </div>
            <div className="space-y-4">
              {filteredCancelled.length === 0 ? (
                <div className="bg-muted/30 border border-border rounded-xl py-12 text-center">
                  <XCircle className="w-12 h-12 mx-auto mb-4 text-foreground/20" /><p className="text-muted-foreground">Aucune commande annulée</p>
                </div>
              ) : (
                filteredCancelled.map(order => (
                  <Card key={order.id} className="bg-muted/30 border-red-500/10 hover:border-red-500/30 transition-colors">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium flex items-center gap-1 text-foreground/70"><Star className="w-3 h-3 text-yellow-500" />Note: {order.buyer_rating?.toFixed(1) || 'N/A'}</span>
                            <Badge className={statusColors[order.current_status]}>{statusLabels[order.current_status]}</Badge>
                          </div>
                          <p className="text-foreground">{flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}</p>
                          <p className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString('fr-FR')}</p>
                          {order.listing_id && <Link to={`/listings/${order.listing_id}`} className="text-[#D4AF37] hover:underline text-xs inline-flex items-center gap-1 mt-0.5"><ExternalLink className="w-3 h-3" />Voir l'annonce</Link>}
                          {order.cancellation_reason && <p className="text-sm text-red-400 mt-2">Raison: {order.cancellation_reason}</p>}
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-red-400 line-through">{formatPrice(order.seller_amount)}</p>
                          {order.current_status === 'refunded' && <Badge className="bg-amber-500/20 text-amber-400 mt-1">Remboursé</Badge>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SellerDashboard;
